<template>
  <div class="entity-search-widget">
    <!-- Trigger Button -->
    <button
      @click="openModal"
      class="btn-primary-sm"
    >
      <FontAwesomeIcon :icon="['fas', 'folder-tree']" />
      Select Entities
      <span v-if="selectedCount > 0" class="bg-white text-primary-500 px-2 py-0.5 rounded-full text-sm font-bold ml-1">
        {{ selectedCount }}
      </span>
    </button>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="cancelSelection"></div>

      <!-- Modal Content -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="relative bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <!-- Modal Header -->
          <div class="bg-primary-100 text-gray-900 px-6 py-4">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon :icon="['fas', 'folder-tree']" />
                Select Entities
              </h2>
              <button @click="cancelSelection" class="text-gray-600 hover:text-gray-900 transition-colors">
                <FontAwesomeIcon :icon="['fas', 'times']" class="text-xl" />
              </button>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <!-- Search and Filters Section -->
            <div class="space-y-4 mb-6">
              <!-- Main Search Controls Grid -->
              <div class="grid grid-cols-3 gap-4">
                <!-- Entity Type Filter -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Entity Category</label>
                  <select
                    v-model="filters.entityType"
                    @change="onEntityTypeChange"
                    class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  >
                    <option :value="null">All Categories</option>
                    <option v-for="type in entityTypes" :key="type.id" :value="type.id">
                      {{ type.type_name }}
                    </option>
                  </select>
                </div>

                <!-- Name Search -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Search by Name</label>
                  <div class="flex items-center bg-white border border-gray-200 rounded-lg px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                    <FontAwesomeIcon :icon="['fas', 'search']" class="text-gray-400 mr-2" />
                    <input
                      v-model="searchText" data-demo-search
                      @input="debouncedSearch"
                      placeholder="Enter entity name..."
                      class="sensitive flex-1 py-2 outline-none text-sm"
                    />
                    <button
                      v-if="searchText"
                      @click="clearSearch"
                      class="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <FontAwesomeIcon :icon="['fas', 'times']" class="text-gray-400" />
                    </button>
                  </div>
                </div>

                <!-- Parent Search -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Search by Parent</label>
                  <div class="relative">
                    <input
                      v-model="parentSearchText" data-demo-search
                      @input="searchParentEntities"
                      @focus="showParentSuggestions = true"
                      @blur="hideParentSuggestions"
                      placeholder="Enter parent entity..."
                      class="sensitive w-full px-3 py-2 pr-8 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    />

                    <!-- Parent Suggestions Dropdown -->
                    <transition name="dropdown">
                      <div v-if="showParentSuggestions && parentSuggestions.length > 0" class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                        <div
                          v-for="parent in parentSuggestions"
                          :key="parent.id"
                          @mousedown="selectParent(parent)"
                          class="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <span class="sensitive text-sm font-medium" :data-spoof-kind="entitySpoofKind(parent.entity_type?.type_name, parent.name)">{{ parent.name }}</span>
                          <span class="text-xs text-gray-500 ml-2">{{ parent.entity_type?.type_name }}</span>
                        </div>
                      </div>
                    </transition>

                    <!-- Selected Parent Display -->
                    <div v-if="filters.parentEntity" class="absolute top-full left-0 right-0 mt-2 flex items-center justify-between p-2.5 bg-primary-50 border border-primary-200 rounded-lg">
                      <span class="text-sm font-medium text-gray-700">Filtering by: <span class="sensitive" :data-spoof-kind="entitySpoofKind(filters.parentEntity.entity_type?.type_name, filters.parentEntity.name)">{{ filters.parentEntity.name }}</span></span>
                      <button @click="clearParent" class="p-1 hover:bg-primary-100 rounded">
                        <FontAwesomeIcon :icon="['fas', 'times']" class="text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tag Filter -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Search by Tags</label>
                <InfoTagFilter
                  v-model="selectedTagIds"
                  v-model:excludedTags="excludedTagIds"
                  v-model:filterMode="tagFilterMode"
                  label="Filter by Tag"
                  button-label="Add Tag"
                  enable-advanced-filters
                  @change="onTagsChange"
                />
              </div>
            </div>

            <!-- Selection Stats Bar -->
            <div class="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg">
              <div class="flex items-center gap-4">
                <span class="text-sm text-gray-600">
                  <span v-if="loading">Searching...</span>
                  <span v-else>{{ totalResults }} entities found</span>
                </span>
                <div v-if="selectedIds.length > 0" class="flex items-center gap-4">
                  <span class="text-sm font-medium text-primary-500">
                    {{ selectedIds.length }} selected
                  </span>
                  <!-- Include Descendants Checkbox -->
                  <label class="flex items-center gap-2" v-if="allowDescendantSelection">
                    <input
                      type="checkbox"
                      v-model="includeSelectedDescendants"
                      class="rounded border-gray-300 accent-primary-500 focus:ring-primary-500"
                    >
                    <span class="text-sm text-gray-700 font-medium">Include descendants of selected</span>
                  </label>
                </div>
              </div>
              <div class="flex gap-2">
                <button
                  @click="selectAll"
                  :disabled="loading || totalResults === 0"
                  class="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Select All
                </button>
                <button
                  @click="clearSelection"
                  :disabled="selectedIds.length === 0"
                  class="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Selection
                </button>
              </div>
            </div>

            <!-- AG Grid Container -->
            <div class="border border-gray-200 rounded-lg overflow-hidden h-[400px]">
              <LoadingIndicator v-if="loading" message="Loading entities" class="h-full" />

              <div v-else-if="!loading && results.length === 0" class="flex flex-col justify-center items-center h-full">
                <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-4xl text-gray-300 mb-3" />
                <p class="text-gray-500">No entities found</p>
              </div>

              <ag-grid-vue
                v-else
                :style="{ height: '100%', width: '100%' }"
                class="ag-theme-alpine"
                :column-defs="columnDefs"
                :row-data="results"
                :default-col-def="defaultColDef"
                :row-selection="rowSelection"
                :suppress-row-click-selection="true"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
              />
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div class="flex justify-between items-center">
              <div class="text-sm text-gray-600">
                <span>{{ selectedIds.length }} {{ selectedIds.length === 1 ? 'entity' : 'entities' }} selected</span>
                <span v-if="includeSelectedDescendants && selectedIds.length > 0" class="ml-2 text-primary-500">
                  (+ descendants)
                </span>
              </div>
              <div class="flex gap-3">
                <button
                  @click="cancelSelection"
                  class="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  @click="confirmSelection"
                  :disabled="selectedIds.length === 0 || processingDescendants"
                  class="btn-primary"
                >
                  <FontAwesomeIcon v-if="!processingDescendants" :icon="['fas', 'check']" />
                  <FontAwesomeIcon v-else :icon="['fas', 'spinner']" class="animate-spin" />
                  {{ processingDescendants ? 'Processing...' : 'Select and Close' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import InfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/InfoTagFilter.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { entitySpoofKind, sensitiveSetFilterParams } from '@/utils/demoSpoof';
import demoProtectedFields from '@/mixins/demoProtectedFields';

export default {
  name: 'EntitySearchWidget',

  components: {
    AgGridVue,
    FontAwesomeIcon,
    InfoTagFilter,
    LoadingIndicator,
  },
  mixins: [demoProtectedFields],
  // Name and parent searches run over practice / provider / facility names,
  // which are tagged. Demo Mode empties them and the tagged inputs are
  // read-only; the entity type and tag filters stay usable. Not persisted.
  demoProtectedFields: ['searchText', 'parentSearchText'],

  props: {
    initialSelection: {
      type: Array,
      default: () => []
    },
    allowDescendantSelection: {
      type: Boolean,
      default: true
    },
    clearSelectionOnClose: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // Modal control
      showModal: false,

      // Search
      searchText: '',

      // Filters
      filters: {
        entityType: null,
        parentEntity: null
      },

      // Parent entity search
      parentSearchText: '',
      parentSuggestions: [],
      showParentSuggestions: false,

      // Entity types
      entityTypes: [],

      // Tags (InfoTagFilter fetches its own option list)
      selectedTagIds: [],
      excludedTagIds: [],
      tagFilterMode: 'or',

      // Results
      results: [],
      totalResults: 0,
      loading: false,

      // Selection
      selectedIds: [],
      selectedCount: 0,
      includeSelectedDescendants: false,
      processingDescendants: false,

      // AG Grid
      gridApi: null,
      columnDefs: [
        {
          field: 'name',
          headerName: 'Name',
          flex: 2,
          minWidth: 200,
          // Entity names are practice / provider / facility identities.
          cellClass: 'sensitive',
          filterParams: { ...sensitiveSetFilterParams },
        },
        {
          field: 'entity_type_name',
          headerName: 'Type',
          flex: 1,
          minWidth: 120,
        },
        {
          field: 'attributes',
          headerName: 'Attributes',
          flex: 2,
          minWidth: 200,
          // Attribute values can be identifiers (TIN, NPI, address).
          cellClass: 'sensitive',
          filterParams: { ...sensitiveSetFilterParams },
          valueGetter: params => {
            return this.getAttributePreview(params.data?.attributes);
          }
        }
      ],
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true
      }
    };
  },

  created() {
    this.debouncedSearch = debounce(this.searchEntities, 300);
    this.debouncedParentSearch = debounce(this.fetchParentSuggestions, 300);

    if (this.initialSelection && this.initialSelection.length > 0) {
      this.selectedIds = [...this.initialSelection];
      this.selectedCount = this.selectedIds.length;
    }
  },

  methods: {
    entitySpoofKind,
    openModal() {
      this.showModal = true;
      this.loadEntityTypes();
    },
    closeModal() {
      this.showModal = false;
      if (this.clearSelectionOnClose) {
        this.clearSelection();
      }
    },

    async loadEntityTypes() {
      try {
        const response = await axios.get(`api/entity-types/`);
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Failed to load entity types:', error);
      }
    },

    async searchEntities() {
      this.loading = true;

      try {
        const params = {};

        if (this.searchText) {
          params.name = this.searchText;
        }

        if (this.filters.entityType) {
          params.entity_type_id = this.filters.entityType;
        }

        if (this.filters.parentEntity) {
          params.parent_id = this.filters.parentEntity.id;
          params.include_descendants = true; // Always include descendants when searching by parent
        }

        if (this.selectedTagIds.length) {
          params.entity_infotag_filters = this.selectedTagIds.join(',');
          params.infotag_filter_mode = this.tagFilterMode;
        }
        if (this.excludedTagIds.length) {
          params.entity_infotag_exclude = this.excludedTagIds.join(',');
        }

        const response = await axios.get(`api/entities/`, { params });

        this.results = response.data.results || response.data;
        this.totalResults = response.data.count || this.results.length;

        // Restore selection after data load
        this.$nextTick(() => {
          if (this.gridApi) {
            this.gridApi.forEachNode(node => {
              if (this.selectedIds.includes(node.data.id)) {
                node.setSelected(true);
              }
            });
          }
        });

      } catch (error) {
        console.error('Search failed:', error);
        this.results = [];
        this.totalResults = 0;
      } finally {
        this.loading = false;
      }
    },

    async searchParentEntities() {
      this.debouncedParentSearch();
    },

    async fetchParentSuggestions() {
      if (!this.parentSearchText || this.parentSearchText.length < 2) {
        this.parentSuggestions = [];
        return;
      }

      try {
        const response = await axios.get(`api/entities/search_parents/`, {
          params: { q: this.parentSearchText }
        });

        this.parentSuggestions = response.data;
      } catch (error) {
        console.error('Parent search failed:', error);
        this.parentSuggestions = [];
      }
    },

    selectParent(parent) {
      this.filters.parentEntity = parent;
      this.parentSearchText = '';
      this.showParentSuggestions = false;
      this.searchEntities();
    },

    clearParent() {
      this.filters.parentEntity = null;
      this.searchEntities();
    },

    hideParentSuggestions() {
      setTimeout(() => {
        this.showParentSuggestions = false;
      }, 200);
    },

    onEntityTypeChange() {
      this.searchEntities();
    },

    onTagsChange() {
      this.searchEntities();
    },

    clearSearch() {
      this.searchText = '';
      this.searchEntities();
    },

    getAttributePreview(attributes) {
      if (!attributes || typeof attributes !== 'object') return '';

      const groups = Object.values(attributes);
      if (!groups.length) return '';

      const firstGroup = groups[0];
      if (!Array.isArray(firstGroup) || !firstGroup.length) return '';

      const preview = firstGroup.slice(0, 2).map(attr => {
        let value = attr.value;
        if (typeof value === 'string' && value.length > 20) {
          value = value.substring(0, 20) + '...';
        }
        return `${attr.display_name}: ${value}`;
      }).join(', ');

      return preview;
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();

      // Restore previous selection
      if (this.selectedIds.length > 0) {
        this.gridApi.forEachNode(node => {
          if (this.selectedIds.includes(node.data.id)) {
            node.setSelected(true);
          }
        });
      }
    },

    onSelectionChanged() {
      if (this.gridApi) {
        const selectedNodes = this.gridApi.getSelectedNodes();
        this.selectedIds = selectedNodes.map(node => node.data.id);
      }
    },

    selectAll() {
      if (this.gridApi) {
        this.gridApi.selectAll();
      }
    },

    clearSelection() {
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
      this.selectedIds = [];
      this.selectedCount = 0;
      this.includeSelectedDescendants = false;
    },

    async getDescendantIds(entityIds) {
      const allDescendantIds = new Set();

      try {
        // Fetch descendants for each selected entity
        for (const entityId of entityIds) {
          const response = await axios.get(`api/entities/`, {
            params: {
              parent_id: entityId,
              include_descendants: true
            }
          });

          const descendants = response.data.results || response.data;
          descendants.forEach(desc => allDescendantIds.add(desc.id));
        }

        return Array.from(allDescendantIds);
      } catch (error) {
        console.error('Failed to fetch descendants:', error);
        return [];
      }
    },

    async confirmSelection() {
      let finalEntityIds = [...this.selectedIds];

      if (this.includeSelectedDescendants && this.selectedIds.length > 0) {
        this.processingDescendants = true;

        try {
          const descendantIds = await this.getDescendantIds(this.selectedIds);
          // Combine selected IDs with descendant IDs (avoiding duplicates)
          const combinedSet = new Set([...this.selectedIds, ...descendantIds]);
          finalEntityIds = Array.from(combinedSet);
        } catch (error) {
          console.error('Error fetching descendants:', error);
        } finally {
          this.processingDescendants = false;
        }
      }

      this.selectedCount = finalEntityIds.length;
      this.$emit('entities-selected', finalEntityIds);
      this.closeModal();
    },

    cancelSelection() {
      this.closeModal();
      this.includeSelectedDescendants = false;
    }
  },
  watch: {
    initialSelection(newSelection) {
      this.selectedIds = newSelection;
      this.selectedCount = this.selectedIds.length;
    }
  }
};
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.2s ease;
}
.dropdown-enter, .dropdown-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
